<template>
  <b-card>
    <h4 class="mb-1">
      Two-Factor Authentication (2FA)
    </h4>
    <p class="text-muted mb-2">
      ใช้แอป Google Authenticator หรือแอป TOTP อื่นๆ เพื่อเพิ่มความปลอดภัยให้บัญชี
    </p>

    <div v-if="loading" class="text-center py-2">
      <b-spinner small />
      กำลังโหลด...
    </div>

    <div v-else>
      <b-alert
        v-if="!status.enabled"
        show
        variant="warning"
      >
        กรุณาเปิดใช้งาน 2FA ก่อนใช้งานระบบ (ระบบบังคับใช้งาน)
      </b-alert>

      <b-alert
        v-if="status.enabled"
        show
        variant="success"
      >
        เปิดใช้งาน 2FA แล้ว — Recovery codes คงเหลือ {{ status.recoveryCodesRemaining }} รหัส
      </b-alert>

      <!-- Disabled: start setup -->
      <div v-if="!status.enabled && !setupData">
        <b-button
          v-if="!mandatory || !setupToken"
          variant="primary"
          @click="startSetup"
        >
          เปิดใช้งาน 2FA
        </b-button>
      </div>

      <!-- Setup pending -->
      <div v-if="setupData && !status.enabled">
        <p class="mb-1">
          สแกน QR Code ด้วยแอป Authenticator
        </p>
        <div class="mb-2">
          <canvas ref="qrCanvas" />
        </div>
        <p class="small text-muted mb-1">
          หรือกรอก Secret ด้วยตนเอง:
        </p>
        <code class="d-block mb-2">{{ setupData.secret }}</code>

        <b-form-group label="รหัส OTP 6 หลัก">
          <b-form-input
            v-model="enableOtp"
            maxlength="6"
            placeholder="123456"
          />
        </b-form-group>
        <b-button
          variant="success"
          class="mr-1"
          :disabled="!enableOtp || submitting"
          @click="confirmEnable"
        >
          ยืนยันเปิดใช้งาน
        </b-button>
        <b-button
          v-if="!mandatory"
          variant="outline-secondary"
          @click="cancelSetup"
        >
          ยกเลิก
        </b-button>
      </div>

      <!-- Enabled: manage recovery codes -->
      <div v-if="status.enabled">
        <b-button
          variant="outline-primary"
          class="mb-1"
          :disabled="submitting"
          @click="regenerateCodes"
        >
          สร้าง Recovery Codes ใหม่
        </b-button>
      </div>

      <!-- Recovery codes display -->
      <b-alert
        v-if="recoveryCodes.length"
        show
        variant="warning"
        class="mt-2"
      >
        <strong>บันทึก Recovery Codes เหล่านี้ไว้ในที่ปลอดภัย (แสดงครั้งเดียว):</strong>
        <ul class="mb-0 mt-1">
          <li
            v-for="code in recoveryCodes"
            :key="code"
          >
            <code>{{ code }}</code>
          </li>
        </ul>
      </b-alert>
    </div>
  </b-card>
</template>

<script>
import QRCode from 'qrcode'
import {
  BCard, BButton, BFormGroup, BFormInput, BAlert, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import twoFactorApi from '@/api/twoFactor'
import { getUserData, clearTwoFaSetupSession } from '@/auth/utils'

export default {
  components: {
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BAlert,
    BSpinner,
  },
  props: {
    mandatory: {
      type: Boolean,
      default: false,
    },
    setupToken: {
      type: String,
      default: '',
    },
    accountType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: true,
      submitting: false,
      status: {
        enabled: false,
        recoveryCodesRemaining: 0,
      },
      setupData: null,
      enableOtp: '',
      recoveryCodes: [],
    }
  },
  mounted() {
    if (this.mandatory && this.setupToken) {
      this.loading = false
      this.startSetup()
      return
    }
    this.fetchStatus()
  },
  methods: {
    toast(title, variant, text) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: { title, icon: 'ShieldIcon', variant, text },
      })
    },
    getErrorMessage(error, fallback = 'เกิดข้อผิดพลาด') {
      const data = error?.response?.data
      if (data?.message) return data.message
      if (data?.error?.message) return data.error.message
      return fallback
    },
    async fetchStatus() {
      this.loading = true
      try {
        const { data } = await twoFactorApi.getStatus()
        this.status = data
      } catch (error) {
        this.toast('ERROR', 'danger', 'ไม่สามารถโหลดสถานะ 2FA ได้')
      } finally {
        this.loading = false
      }
    },
    async renderQr() {
      if (!this.setupData?.otpauthUrl || !this.$refs.qrCanvas) return
      await QRCode.toCanvas(this.$refs.qrCanvas, this.setupData.otpauthUrl, {
        width: 200,
        margin: 2,
      })
    },
    async startSetup() {
      this.submitting = true
      try {
        const { data } = this.mandatory && this.setupToken
          ? await twoFactorApi.setupWithToken(this.setupToken, this.accountType)
          : await twoFactorApi.setup()
        this.setupData = data
        this.enableOtp = ''
        this.$nextTick(() => this.renderQr())
      } catch (error) {
        const msg = this.getErrorMessage(error, 'ไม่สามารถตั้งค่า 2FA ได้')
        if (this.mandatory && this.setupToken && /หมดอายุ|ไม่ถูกต้อง/.test(msg)) {
          clearTwoFaSetupSession()
          this.$router.replace({ name: 'auth-login' })
          return
        }
        this.toast('ERROR', 'danger', msg)
      } finally {
        this.submitting = false
      }
    },
    cancelSetup() {
      this.setupData = null
      this.enableOtp = ''
    },
    async confirmEnable() {
      this.submitting = true
      try {
        const { data } = this.mandatory && this.setupToken
          ? await twoFactorApi.enableWithToken(this.setupToken, this.accountType, this.enableOtp)
          : await twoFactorApi.enable(this.enableOtp)

        this.recoveryCodes = data.recoveryCodes || []
        this.setupData = null
        this.enableOtp = ''

        if (this.mandatory && this.setupToken && data.token) {
          this.$emit('completed', data)
          return
        }

        sessionStorage.setItem('adminTwoFaOk', '1')
        const userData = getUserData()
        if (userData) {
          userData.twoFactorEnabled = true
          localStorage.setItem('userData', JSON.stringify(userData))
        }

        await this.fetchStatus()
        this.toast('สำเร็จ', 'success', 'เปิดใช้งาน 2FA แล้ว')
      } catch (error) {
        const msg = this.getErrorMessage(error, 'รหัส OTP ไม่ถูกต้อง')
        if (this.mandatory && this.setupToken && /หมดอายุ|ไม่ถูกต้อง/.test(msg)) {
          clearTwoFaSetupSession()
          this.$router.replace({ name: 'auth-login' })
          return
        }
        this.toast('ERROR', 'danger', msg)
      } finally {
        this.submitting = false
      }
    },
    async regenerateCodes() {
      this.submitting = true
      try {
        const { data } = await twoFactorApi.regenerateRecoveryCodes()
        this.recoveryCodes = data.recoveryCodes || []
        await this.fetchStatus()
        this.toast('สำเร็จ', 'success', 'สร้าง Recovery Codes ใหม่แล้ว')
      } catch (error) {
        this.toast('ERROR', 'danger', error.response?.data?.message || 'ไม่สามารถสร้าง Recovery Codes ได้')
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>
